import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../stores';
import './Admin.css';
const providerTypes = [
    { value: 'google', label: 'Google' },
    { value: 'facebook', label: 'Facebook (Meta)' },
    { value: 'instagram', label: 'Instagram (Meta)' },
    { value: 'microsoft', label: 'Microsoft' },
    { value: 'github', label: 'GitHub' },
];
export function SocialProviders() {
    const { accessToken } = useAuthStore();
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        displayName: '',
        providerType: 'google',
        clientId: '',
        clientSecret: '',
        isActive: false,
        description: '',
    });
    const fetchProviders = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/auth-providers', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Failed to fetch social login providers');
            const data = await response.json();
            setProviders(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load social login providers');
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    useEffect(() => {
        fetchProviders();
    }, [fetchProviders]);
    const handleOpenModal = (provider) => {
        if (provider) {
            setEditingProvider(provider);
            setFormData({
                name: provider.name,
                displayName: provider.displayName || '',
                providerType: provider.providerType,
                clientId: '',
                clientSecret: '',
                isActive: provider.isActive,
                description: provider.description || '',
            });
        }
        else {
            setEditingProvider(null);
            setFormData({
                name: 'google',
                displayName: 'Google',
                providerType: 'google',
                clientId: '',
                clientSecret: '',
                isActive: false,
                description: '',
            });
        }
        setShowModal(true);
        setError('');
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingProvider(null);
        setError('');
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setActionLoading(editingProvider?.id || 0);
        try {
            const url = editingProvider
                ? `/api/admin/auth-providers/${editingProvider.id}`
                : '/api/admin/auth-providers';
            const method = editingProvider ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(formData),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to save provider');
            }
            await fetchProviders();
            handleCloseModal();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save provider');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleToggleActive = async (provider) => {
        setActionLoading(provider.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/auth-providers/${provider.id}/toggle`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to toggle provider');
            }
            await fetchProviders();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to toggle provider');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDelete = async (provider) => {
        if (!confirm(`"${provider.displayName || provider.name}" provider'ını silmek istediğinize emin misiniz?`)) {
            return;
        }
        setActionLoading(provider.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/auth-providers/${provider.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to delete provider');
            }
            await fetchProviders();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete provider');
        }
        finally {
            setActionLoading(null);
        }
    };
    const typeLabel = (type) => providerTypes.find((p) => p.value === type)?.label || type;
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "Social Login" }), _jsx("p", { children: "OAuth sa\u011Flay\u0131c\u0131lar\u0131n\u0131 y\u00F6netin (Client ID / Secret). Kullan\u0131c\u0131 ba\u011Flar\u0131 Identity AspNetUserLogins tablosunda tutulur." })] }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "+ Yeni Provider Ekle" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["Auth Providers (", providers.length, ")"] }) }), providers.length === 0 ? (_jsxs("div", { className: "admin-empty-state", children: [_jsx("p", { children: "Hen\u00FCz social login provider tan\u0131mlanmam\u0131\u015F." }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "\u0130lk Provider'\u0131 Ekle" })] })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Ad" }), _jsx("th", { children: "Tip" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "\u0130\u015Flemler" })] }) }), _jsx("tbody", { children: providers.map((provider) => (_jsxs("tr", { children: [_jsx("td", { children: _jsxs("div", { className: "provider-info", children: [_jsxs("div", { className: "provider-name-row", children: [_jsx("span", { className: "provider-name", children: provider.displayName || provider.name }), !provider.hasClientId && (_jsxs("span", { className: "provider-missing-api-key-badge", title: "Client ID tan\u0131ml\u0131 de\u011Fil", children: [_jsx(ExclamationTriangleIcon, { "aria-hidden": "true" }), "Client ID yok"] }))] }), provider.description && (_jsx("span", { className: "provider-description", children: provider.description }))] }) }), _jsx("td", { children: _jsx("span", { className: "provider-type-badge", children: typeLabel(provider.providerType) }) }), _jsx("td", { children: _jsx("button", { className: `admin-toggle-btn ${provider.isActive ? 'active' : ''}`, onClick: () => handleToggleActive(provider), disabled: actionLoading === provider.id, title: provider.isActive ? 'Deaktif Et' : 'Aktif Et', children: provider.isActive ? '✓ Aktif' : '✗ Pasif' }) }), _jsx("td", { children: _jsxs("div", { className: "admin-action-buttons", children: [_jsx("button", { className: "admin-edit-btn", onClick: () => handleOpenModal(provider), disabled: actionLoading === provider.id, title: "D\u00FCzenle", children: "\u270F\uFE0F" }), _jsx("button", { className: "admin-delete-btn", onClick: () => handleDelete(provider), disabled: actionLoading === provider.id, title: "Sil", children: "\uD83D\uDDD1\uFE0F" })] }) })] }, provider.id))) })] }) }))] }), showModal && (_jsx("div", { className: "admin-modal-overlay", onClick: handleCloseModal, children: _jsxs("div", { className: "admin-modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "admin-modal-header", children: [_jsx("h2", { children: editingProvider ? 'Provider Düzenle' : 'Yeni Provider' }), _jsx("button", { className: "admin-modal-close", onClick: handleCloseModal, children: "\u00D7" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "admin-form", children: [_jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Name (unique key)", _jsx("input", { className: "admin-form-input", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), required: true, placeholder: "google" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Display Name", _jsx("input", { className: "admin-form-input", value: formData.displayName, onChange: (e) => setFormData({ ...formData, displayName: e.target.value }), placeholder: "Google" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Provider Type", _jsx("select", { className: "admin-form-select", value: formData.providerType, onChange: (e) => setFormData({ ...formData, providerType: e.target.value }), children: providerTypes.map((t) => (_jsx("option", { value: t.value, children: t.label }, t.value))) })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Client ID", _jsx("input", { className: "admin-form-input", value: formData.clientId, onChange: (e) => setFormData({ ...formData, clientId: e.target.value }), required: !editingProvider, placeholder: editingProvider?.hasClientId ? 'Boş bırakırsanız mevcut değer korunur' : 'OAuth Client ID', autoComplete: "off" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Client Secret", _jsx("input", { className: "admin-form-input", type: "password", value: formData.clientSecret, onChange: (e) => setFormData({ ...formData, clientSecret: e.target.value }), placeholder: editingProvider?.hasClientSecret
                                                    ? 'Boş bırakırsanız mevcut değer korunur'
                                                    : 'Opsiyonel (GIS id_token için gerekmez)', autoComplete: "new-password" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Description", _jsx("textarea", { className: "admin-form-textarea", value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), rows: 2 })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-checkbox", children: [_jsx("input", { type: "checkbox", checked: formData.isActive, onChange: (e) => setFormData({ ...formData, isActive: e.target.checked }) }), "Aktif (login sayfas\u0131nda g\u00F6ster)"] }) }), _jsxs("div", { className: "admin-modal-actions", children: [_jsx("button", { type: "button", className: "admin-btn", onClick: handleCloseModal, children: "\u0130ptal" }), _jsx("button", { type: "submit", className: "admin-btn admin-btn-primary", disabled: actionLoading !== null, children: "Kaydet" })] })] })] }) }))] }));
}
